import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Clock, BookOpen, CheckCircle } from 'lucide-react';
import { Course, Lesson } from '@/types';
import { api } from '../utils/apiInterceptor';
import './CourseDetailPage.scss';

export default function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(
    new Set()
  );
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCourseDetails();
    }
  }, [id]);

  // Auto-refresh if lessons are still generating
  useEffect(() => {
    if (course && course.lessons) {
      const hasGeneratingLessons = course.lessons.some(lesson => {
        const status = (lesson as any).status;
        return status === 'PENDING' || status === 'GENERATING';
      });

      if (hasGeneratingLessons && !refreshInterval) {
        console.log('🔄 Setting up auto-refresh for generating lessons');
        const interval = setInterval(() => {
          fetchCourseDetails(false); // Background refresh, don't show loading
        }, 3000); // Refresh every 3 seconds for faster updates
        setRefreshInterval(interval);
      } else if (!hasGeneratingLessons && refreshInterval) {
        console.log('✅ All lessons complete - stopping auto-refresh');
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }

    // Cleanup on unmount
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [course, refreshInterval]);

  const fetchCourseDetails = async (showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const response = await api.get(`/courses/${id}`);

      if (response.data) {
        // Handle wrapped response format
        const courseData = response.data.success ? response.data.data : response.data;
        setCourse(courseData);

        // Extract completed lessons from the course data
        const completedLessonIds = new Set<string>();
        
        if (courseData.lessons) {
          courseData.lessons.forEach((lesson: any) => {
            // Progress is an array, check if there's any progress entry with completedAt
            if (lesson.progress && Array.isArray(lesson.progress)) {
              const hasCompleted = lesson.progress.some((p: any) => p.completedAt);
              if (hasCompleted) {
                completedLessonIds.add(lesson.id);
              }
            }
          });
        }
        setCompletedLessons(completedLessonIds);

        // Check if course is completed based on enrollment data
        const enrollment = courseData.enrollments?.[0];
        setCourseCompleted(!!enrollment?.completedAt);
      } else {
        throw new Error('Failed to load course');
      }
    } catch (err: any) {
      console.error('Error fetching course:', err);
      setError(err.message || 'Failed to fetch course details');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleLessonComplete = (lessonId: string) => {
    setCompletedLessons(prev => new Set([...prev, lessonId]));
  };

  const handleMarkCourseComplete = async () => {
    if (!course) return;

    try {
      // Since course completion is automatic when all lessons are completed,
      // we'll mark all lessons as complete if the user wants to mark the course as complete
      const incompleteLessons =
        course.lessons?.filter(lesson => !completedLessons.has(lesson.id)) ||
        [];

      // Mark all incomplete lessons as complete
      for (const lesson of incompleteLessons) {
        await api.post(`/lessons/${lesson.id}/complete`);
      }

      // Refresh course data to get updated completion status
      await fetchCourseDetails(false);
    } catch (error) {
      console.error('Failed to mark course as complete:', error);
    }
  };

  const handlePublishToggle = async () => {
    if (!course) return;

    setPublishing(true);
    try {
      const isCurrentlyPublished = course.status === 'published';
      const response = await api.patch(`/courses/${course.id}/publish`, {
        publish: !isCurrentlyPublished,
      });

      if (response.data.success) {
        setCourse(prev =>
          prev ? { ...prev, status: response.data.data.status } : null
        );
      }
    } catch (error: any) {
      console.error('Failed to toggle course publication:', error);
      setError(error.response?.data?.error || 'Failed to update course status');
    } finally {
      setPublishing(false);
    }
  };

  const calculateProgress = () => {
    if (!course?.lessons) return 0;
    return Math.round((completedLessons.size / course.lessons.length) * 100);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  if (loading) {
    return (
      <div className="course-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading course...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="course-detail-page">
        <div className="container">
          <div className="error-state">
            <h1>Course Not Found</h1>
            <p>{error || "The course you're looking for doesn't exist."}</p>
            <button
              className="back-button"
              onClick={() => navigate('/explore')}
            >
              Back to Courses
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="course-detail-page">
      <div className="container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="header">
            <div className="header-nav">
              <button className="back-button" onClick={() => navigate(-1)}>
                <ChevronLeft size={16} />
                Back
              </button>

              <button
                className={`course-publish-btn ${course?.status === 'published' ? 'published' : ''}`}
                onClick={handlePublishToggle}
                disabled={publishing}
              >
                <BookOpen size={18} />
                <span>
                  {publishing
                    ? 'Updating...'
                    : course?.status === 'published'
                      ? 'Make Private'
                      : 'Publish Course'}
                </span>
              </button>
            </div>

            <div className="detail-course-header">
              <div className="detail-course-meta">
                <span className="detail-topic">{course.topic}</span>
                <span className={`detail-difficulty ${course.difficulty || 'basic'}`}>
                  {course.difficulty?.replace('_', ' ') || 'Basic'}
                </span>
              </div>

              <h1 className="detail-course-title">{course.title}</h1>
              <p className="detail-description">{course.description}</p>

              <div className="stats">
                <div className="stat">
                  <Clock size={16} />
                  {course.estimatedHours}h
                </div>
                <div className="stat">
                  <BookOpen size={16} />
                  {course._count?.lessons || 0} lessons
                </div>
                <div className="stat progress">
                  {calculateProgress()}% complete
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="content">
            {/* Lessons */}
            <div className="lessons-section">
              <h2>Course Content</h2>

              <div className="lessons-list">
                {course.lessons?.map((lesson: Lesson, index: number) => {
                  const isCompleted = completedLessons.has(lesson.id);
                  const status = (lesson as any).status || 'PENDING';
                  const isPending = status === 'PENDING';
                  const isGenerating = status === 'GENERATING';
                  const isReady = status === 'READY';
                  const hasFailed = status === 'FAILED';
                  const isAvailable = isReady;

                  return (
                    <div
                      key={lesson.id}
                      className={`lesson-item ${isCompleted ? 'completed' : ''} ${isGenerating ? 'generating' : ''} ${hasFailed ? 'failed' : ''} ${isPending ? 'pending' : ''}`}
                    >
                      <div className="lesson-number">
                        {isCompleted ? (
                          <CheckCircle size={18} />
                        ) : hasFailed ? (
                          <span className="error-icon">⚠</span>
                        ) : isGenerating ? (
                          <div className="generating-spinner"></div>
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>

                      <div className="lesson-content">
                        <h3>{lesson.title}</h3>
                        <div className="lesson-meta">
                          {hasFailed ? (
                            <span className="failed-text">
                              Generation failed
                            </span>
                          ) : isGenerating ? (
                            <span className="generating-text">
                              Generating content...
                            </span>
                          ) : isPending ? (
                            <span className="pending-text">
                              Waiting to generate...
                            </span>
                          ) : isReady ? (
                            <span>
                              {formatDuration(lesson.estimatedMinutes)}
                            </span>
                          ) : (
                            <span>Unknown status</span>
                          )}
                        </div>
                      </div>

                      <button
                        className={`start-button ${hasFailed ? 'retry-button' : ''}`}
                        onClick={() =>
                          isAvailable ? navigate(`/lessons/${lesson.id}`) : null
                        }
                        disabled={!isAvailable && !hasFailed}
                      >
                        {hasFailed
                          ? 'Retry'
                          : isGenerating
                            ? 'Generating...'
                            : isPending
                              ? 'Pending'
                              : isCompleted
                                ? 'Review'
                                : isAvailable
                                  ? 'Start'
                                  : 'Waiting'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Course Progress - Inline Style */}
            <div className="course-progress-section">
              <div className="progress-info">
                <h3>Course Progress</h3>
                <p>
                  {calculateProgress()}% complete • {completedLessons.size} of{' '}
                  {course?.lessons?.length || 0} lessons
                </p>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${calculateProgress()}%` }}
                  ></div>
                </div>
              </div>

              <div className="action-buttons">
                {courseCompleted ? (
                  <div className="completed-badge">
                    <CheckCircle size={20} />
                    Course Completed
                  </div>
                ) : (
                  <button
                    className="mark-complete-button"
                    onClick={handleMarkCourseComplete}
                  >
                    <CheckCircle size={20} />
                    Mark Course Complete
                  </button>
                )}
                <button className="continue-button">Continue Learning</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
